/** *****************************************************************************
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2020, 2025. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 ****************************************************************************** */

// ----------------------------------------------------------------------------

var KP_SERVICE_ID = "keyprotect";
var HV_SERVICE_ID = "hashicorpvault";
var SM_SERVICE_ID = "secretsmanager";
var KP_SERVICE_NAME = "Key Protect";
var HV_SERVICE_NAME = "HashiCorp Vault";
var SM_SERVICE_NAME = "Secrets Manager";
var KP_REFERENCE_TYPE = "kp";
var HV_REFERENCE_TYPE = "hv";
var SM_REFERENCE_TYPE = "sm";
var VTI_REFERENCE_TYPE = "vault";
var ANY_REFERENCE_TYPE = "any";
var KP_PROVIDER_TYPE = "kp";
var HV_PROVIDER_TYPE = "hv";
var SM_PROVIDER_TYPE = "sm";
var REF_SM_PROVIDER_TYPE = "ref-sm";
var VTI_PROVIDER_TYPE = "vault";
var ANY_PROVIDER_TYPE = "any";
var CRN_PROVIDER_TYPE = "crn";
var _CRN_REFERENCE_TEMPLATE_ = "^(?<reference>crn:v1:(?:bluemix|staging):public:secrets-manager:(?<region>[a-zA-Z0-9-]*)\\b:a/(?<account_id>[0-9a-fA-F]*)\\b:(?<instance_id>[0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12})\\b:secret:(?<secret_id>[0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12}))$";
var _SECRET_REGEX_CRN = new RegExp(_CRN_REFERENCE_TEMPLATE_);
var _REF_SM_REFERENCE_TEMPLATE = "^ref:\\/\\/secrets-manager\\.((?<account_id>[a-z0-9]{32})[.])?(?<region>([a-z]{2}-)?[a-z0-9]{1,8})[.](?<resource_group>[^.\\/]+)[.](?<instance_name>[^.\\/]+)[\\/](?<secret_group>[^\\/]+)\\/(?<secret_name>[^\\/?]+)(\\?key=(?<kv_key>.*))?$";
var _REF_SM_REGEX = new RegExp(_REF_SM_REFERENCE_TEMPLATE);
// ref://secrets-manager.ab0576...4911a2.eu-de.EU-RG.SM-1/default/api-key?key=api-key
// \_/   \_____________/ \_____________/ \______________/ \_____________/ \_____/
//  |           |               |               |               |            |
// scheme     service       optional         instance          path       optional
//             type         accountid       attributes      attributes   parameters
// e.g.
// ref://secrets-manager.eu-gb.Default.Secrets%20Manager-1x/Default/secret-key-value-pair-01?key=service-id-api-key
// Where user-authorable parts are url encoded e.g "Secrets Manager-1x" -> "Secrets%20Manager-1x" with %20

// Used by search filter on PropertyTable. Ensure it contains all possible ref types
var SECRET_REGEX = new RegExp("(^{".concat(VTI_REFERENCE_TYPE, "::)|(^{").concat(KP_REFERENCE_TYPE, "::)|(^{").concat(HV_REFERENCE_TYPE, "::)|(^{").concat(SM_REFERENCE_TYPE, "::)|(").concat(_CRN_REFERENCE_TEMPLATE_, ")|(^ref:\\/\\/secrets-manager.)|"));
var AUTH_TOKEN = "token";
var AUTH_APPROLE = "approle";
var AUTH_ROLE_ID = "role_id";
var AUTH_SECRET_ID = "secret_id";
var TOOLCARD_PREFIX = "toolcard-";
var SECRETS_API_PATH = "/devops/api/v1/secrets";

// ----------------------------------------------------------------------------

function isCrnReference(value) {
  return _SECRET_REGEX_CRN.test(value);
}
function composeInstanceUrl(secretCrn) {
  var instanceCrn = encodeURIComponent("".concat(secretCrn.split(":secret:")[0], "::"));
  return "/services/secrets-manager/".concat(instanceCrn, "?paneId=manageSecrets#/manageSecrets");
}
function parseValue(value) {
  if (value && value.startsWith("{") && value.substring(value.length - 1) === "}") {
    if (value.startsWith("{".concat(VTI_REFERENCE_TYPE, "::"))) {
      // Parse VTI reference or Hint
      var innerValue = value.substring("{".concat(VTI_REFERENCE_TYPE, "::").length, value.length - 1);
      var parts = innerValue.split(".");
      if (parts.length === 1) {
        // Secret hint
        return {
          toolcard: true,
          toolcardName: "",
          group: "",
          secret: parts[0]
        };
      }
      // VTI reference
      return {
        toolcard: true,
        toolcardName: parts[0],
        group: parts[2] && parts[1],
        secret: parts[2] || parts[1],
        field: parts[3]
      };
    }
    if (value.startsWith("{".concat(KP_REFERENCE_TYPE, "::"))) {
      // Parse KP reference
      var _innerValue = value.substring("{".concat(KP_REFERENCE_TYPE, "::").length, value.length - 1);
      var _parts = _innerValue.split(".");
      return {
        toolcard: false,
        provider: KP_REFERENCE_TYPE,
        region: _parts[0],
        resource_group: _parts[1],
        instance: _parts[2],
        secret: _parts[3]
      };
    }
    if (value.startsWith("{".concat(SM_REFERENCE_TYPE, "::"))) {
      // Parse SM reference
      var _innerValue2 = value.substring("{".concat(SM_REFERENCE_TYPE, "::").length, value.length - 1);
      var _parts2 = _innerValue2.split(".");
      return {
        toolcard: false,
        provider: SM_REFERENCE_TYPE,
        region: _parts2[0],
        resource_group: _parts2[1],
        instance: _parts2[2],
        group: _parts2[3],
        secret: _parts2[4],
        field: _parts2[5]
      };
    }
  } else if (value && value.startsWith("ref://secrets-manager.")) {
    var result = _REF_SM_REGEX.exec(value);
    if (result) {
      var _result$groups = result.groups,
        account_id = _result$groups.account_id,
        region = _result$groups.region,
        resource_group = _result$groups.resource_group,
        instance_name = _result$groups.instance_name;
      var _result$groups2 = result.groups,
        secret_group = _result$groups2.secret_group,
        secret_name = _result$groups2.secret_name,
        kv_key = _result$groups2.kv_key;
      secret_group = decodeURIComponent(secret_group);
      secret_name = decodeURIComponent(secret_name);
      kv_key = !kv_key ? "" : decodeURIComponent(kv_key);
      return {
        toolcard: true,
        provider: REF_SM_PROVIDER_TYPE,
        account_id: account_id || "",
        region: region,
        resource_group: decodeURIComponent(resource_group),
        instance_name: decodeURIComponent(instance_name),
        secret_group: secret_group,
        secret_name: secret_name,
        secret: "".concat(secret_group, "/").concat(secret_name).concat(!kv_key ? "" : "?key=".concat(kv_key)),
        kv_key: kv_key,
        secret_tag: "ref:".concat(secret_group, "/").concat(secret_name).concat(!kv_key ? "" : "?key=".concat(kv_key))
      };
    }
  } else {
    var _result = _SECRET_REGEX_CRN.exec(value);
    if (_result) {
      var secret_id = _result.groups.secret_id;
      return {
        toolcard: true,
        provider: CRN_PROVIDER_TYPE,
        instanceCrn: "".concat(value.split("secret:").slice(0, -1), ":"),
        secret: "crn:".concat(secret_id),
        instanceUrl: composeInstanceUrl(value)
      };
    }
  }
  return null;
}
export default {
  KP_SERVICE_ID: KP_SERVICE_ID,
  HV_SERVICE_ID: HV_SERVICE_ID,
  SM_SERVICE_ID: SM_SERVICE_ID,
  KP_SERVICE_NAME: KP_SERVICE_NAME,
  HV_SERVICE_NAME: HV_SERVICE_NAME,
  SM_SERVICE_NAME: SM_SERVICE_NAME,
  KP_REFERENCE_TYPE: KP_REFERENCE_TYPE,
  HV_REFERENCE_TYPE: HV_REFERENCE_TYPE,
  SM_REFERENCE_TYPE: SM_REFERENCE_TYPE,
  VTI_REFERENCE_TYPE: VTI_REFERENCE_TYPE,
  ANY_REFERENCE_TYPE: ANY_REFERENCE_TYPE,
  KP_PROVIDER_TYPE: KP_PROVIDER_TYPE,
  HV_PROVIDER_TYPE: HV_PROVIDER_TYPE,
  SM_PROVIDER_TYPE: SM_PROVIDER_TYPE,
  REF_SM_PROVIDER_TYPE: REF_SM_PROVIDER_TYPE,
  VTI_PROVIDER_TYPE: VTI_PROVIDER_TYPE,
  ANY_PROVIDER_TYPE: ANY_PROVIDER_TYPE,
  CRN_PROVIDER_TYPE: CRN_PROVIDER_TYPE,
  SECRETS_API_PATH: SECRETS_API_PATH,
  AUTH_TOKEN: AUTH_TOKEN,
  AUTH_APPROLE: AUTH_APPROLE,
  AUTH_ROLE_ID: AUTH_ROLE_ID,
  AUTH_SECRET_ID: AUTH_SECRET_ID,
  TOOLCARD_PREFIX: TOOLCARD_PREFIX,
  SECRET_REGEX: SECRET_REGEX,
  parseValue: parseValue,
  isCrnReference: isCrnReference
};

// ----------------------------------------------------------------------------